import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { useSelector } from 'react-redux';

import { useIntl } from 'ha/i18n';
import { useMediaQuery } from 'ha/helpers/MediaQuery';
import { Grid, Card, Typography, units } from '@hbf/dsl';
import { CONTACT } from 'ha/constants/pageNames';
import { Page } from 'ha/modules/Page';
import { defaultFont } from 'ha/constants/fonts/default';
import { sharpSansBold } from 'ha/constants/fonts/sourceSansHeaders';
import { useFeatureFlags } from 'ha/modules/FeatureFlags';
import { LandlordType, UserType } from 'ha/constants/UserType';
import { getUserType } from 'ha/modules/AuthLogic/selectors';
import { Conversation } from '@ha/api/v2/types';
import { ConversationState } from '@ha/api/v2/types/Conversation';

import BackgroundMobile from 'ha/img-src/contact-us/Background_Mobile.png';
import BackgroundDesktop from 'ha/img-src/contact-us/Background_Desktop.png';
import { InboxContext } from '../../InboxNext/context/InboxContext';

import { ContactUsMetaData } from './ContactUsMetaData';
import { OperatingHours } from './OperatingHours';
import { PhoneBook } from './PhoneBook';
import { AddressDetails } from './AddressDetails';
import { ChatNow } from './ChatNow';
import { EmailInfo } from './EmailInfo';
import { CheckFAQs } from './CheckFAQs';

const useStyles = makeStyles()(theme => ({
  header: {
    height: units.rem(18),

    [theme.breakpoints.down('md')]: {
      height: units.rem(14),
    },
  },
  headerTitle: {
    fontFamily: sharpSansBold.fontFamily,
  },
  subTitle: {
    lineHeight: units.rem(1.63),
    textAlign: 'justify',
  },
  container: {
    maxWidth: units.rem(41),
    margin: '0 auto',
    marginBottom: units.rem(3.6),

    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
      marginBottom: theme.spacing(2),
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
    },
  },
  card: {
    padding: units.yx(1.125, 1.0),
    border: units.border(1, 'solid', theme.palette.grey[300]),

    [theme.breakpoints.down('md')]: {
      padding: units.yx(1.125, 1),
    },
  },
  picture: {
    position: 'absolute',
    top: theme.customSpacing.navHeightLg,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: -1,

    [theme.breakpoints.down('md')]: {
      top: theme.customSpacing.navHeight,
    },
  },
  image: {
    width: '100%',
    maxHeight: '300px',

    [theme.breakpoints.down('sm')]: {
      maxHeight: '240px',
    },
  },
}));

const ALLOWEDUSERTYPES = [
  LandlordType.PRIVATE_LANDLORD,
  LandlordType.RENTAL_COMPANY,
  LandlordType.OTHER,
  UserType.PENDING,
  UserType.LANDLORD,
  UserType.QUARATINE,
  UserType.UNDER_REVIEW,
];

const ALLOWEDCONVERSATIONTYPES = [
  ConversationState.PaidState,
  ConversationState.CancelledState,
  ConversationState.CancellationOngoing,
  ConversationState.PaymentOngoingState,
  ConversationState.RequestedState,
];

export const ContactUs: React.FC = () => {
  const { classes } = useStyles();
  const { T } = useIntl();
  const { md } = useMediaQuery();
  const { hideContactPhoneNumber } = useFeatureFlags();
  const { conversations, fetchConversations } = InboxContext.useContext();
  const userType = useSelector(getUserType);

  const [hasActiveBooking, setHasActiveBooking] =
    React.useState<boolean>(false);

  const isAllowedUserType = ALLOWEDUSERTYPES.includes(
    userType as UserType | LandlordType,
  );

  const findActiveBooking = React.useCallback(() => {
    const isActiveBookState = conversations?.some(
      (conversation: Conversation) =>
        ALLOWEDCONVERSATIONTYPES.includes(conversation.state),
    );

    setHasActiveBooking(isActiveBookState);
  }, [conversations]);

  React.useEffect(() => {
    findActiveBooking();
  }, [conversations, findActiveBooking]);

  React.useEffect(() => {
    fetchConversations(true);
  }, [fetchConversations]);

  const showPhoneNumber = isAllowedUserType || hasActiveBooking;

  let showPhoneDetails;
  if (hideContactPhoneNumber === 'on') {
    showPhoneDetails = showPhoneNumber ? (
      <Grid item xs={12}>
        <Card elevation={0} className={classes.card}>
          <PhoneBook />
        </Card>
      </Grid>
    ) : (
      <Grid item xs={12}>
        <Card elevation={0} className={classes.card}>
          <CheckFAQs />
        </Card>
      </Grid>
    );
  } else {
    showPhoneDetails = (
      <Grid item xs={12}>
        <Card elevation={0} className={classes.card}>
          <PhoneBook />
        </Card>
      </Grid>
    );
  }

  return (
    <Page name={CONTACT} activeFonts={[defaultFont, sharpSansBold]}>
      <ContactUsMetaData />
      <picture className={classes.picture}>
        <source srcSet={BackgroundMobile} media="(max-width: 600px)" />
        <img src={BackgroundDesktop} className={classes.image} alt="" />
      </picture>
      <Grid
        container
        alignItems="center"
        justifyContent="center"
        className={classes.header}
      >
        <Grid item>
          <Typography.H1 className={classes.headerTitle}>
            {T('contact_us_header')}
          </Typography.H1>
        </Grid>
      </Grid>
      <div className={classes.container}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography.Paragraph className={classes.subTitle}>
              {T('contact_us_subtext')}
            </Typography.Paragraph>
          </Grid>
          <Grid item xs={md ? 6 : 12}>
            <Grid container direction="column" spacing={1}>
              {!md && (
                <Grid item xs={12}>
                  <Card elevation={0} className={classes.card}>
                    <OperatingHours />
                  </Card>
                </Grid>
              )}
              <Grid item xs={12}>
                <Card elevation={0} className={classes.card}>
                  <ChatNow />
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card elevation={0} className={classes.card}>
                  <EmailInfo />
                </Card>
              </Grid>

              {showPhoneDetails}

              {!md && (
                <Grid item xs={12}>
                  <Card
                    elevation={0}
                    className={classes.card}
                    data-test-locator="Company Info"
                  >
                    <AddressDetails />
                  </Card>
                </Grid>
              )}
            </Grid>
          </Grid>
          {md && (
            <Grid item xs={6}>
              <Grid container direction="column" spacing={1}>
                <Grid item xs={12}>
                  <Card elevation={0} className={classes.card}>
                    <OperatingHours />
                  </Card>
                </Grid>
                <Grid item xs={12}>
                  <Card
                    elevation={0}
                    className={classes.card}
                    data-test-locator="Company Info"
                  >
                    <AddressDetails />
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </div>
    </Page>
  );
};
